import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Target, Check, CalendarDays, TrendingUp, TrendingDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Input } from './ui/input';
import { Button } from './ui/button';

const AVG_MONTH_DAYS = 30.44;
const COUNT_KPIS = new Set([
  'fa_presented','fa_sold_total','fa_sold_same_day','fa_sold_previous',
  'arches_total','arches_same_day','arches_previous',
  'consults_scheduled','consults_showed','inbound_calls',
  'total_revenue','collections_eod','surgical_production','total_collected'
]);

export const computeProratedGoal = (monthlyGoal, goalKey, periodDays) => {
  if (monthlyGoal === null || monthlyGoal === undefined || monthlyGoal === '') return null;
  const mg = Number(monthlyGoal);
  if (isNaN(mg) || mg <= 0) return null;
  if (!COUNT_KPIS.has(goalKey) || !periodDays || periodDays <= 0) return mg;
  return Math.round(mg * periodDays / AVG_MONTH_DAYS * 10) / 10;
};

export const KpiCard = ({
  label, value, subValue, subLabel, primary, format, index = 0,
  goalKey, monthlyGoals, onGoalChange, periodDays, locationCount
}) => {
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalInput, setGoalInput] = useState('');
  const inputRef = useRef();
  const navigate = useNavigate();

  const fmtVal = (v, fmt) => {
    if (v === null || v === undefined) return '\u2014';
    const n = Number(v);
    if (fmt === 'currency') return '$' + n.toLocaleString('en-US', { maximumFractionDigits: 0 });
    if (fmt === 'percent') return n.toFixed(1) + '%';
    if (fmt === 'days') return n.toFixed(1) + 'd';
    return n.toLocaleString();
  };

  const monthlyGoal = goalKey && monthlyGoals ? monthlyGoals[goalKey] : null;
  const hasGoal = monthlyGoal !== null && monthlyGoal !== undefined && monthlyGoal !== '';
  const proratedGoal = hasGoal ? computeProratedGoal(monthlyGoal, goalKey, periodDays) : null;
  const numValue = value !== null && value !== undefined ? Number(value) : null;
  const meetsGoal = hasGoal && proratedGoal !== null && numValue !== null ? numValue >= proratedGoal : null;
  const pctOfGoal = hasGoal && proratedGoal !== null && proratedGoal > 0 && numValue !== null
    ? Math.round(numValue / proratedGoal * 100) : null;
  const isCountKpi = COUNT_KPIS.has(goalKey);
  const multiLocation = (locationCount || 0) > 1;

  // Left border accent — only if has goal
  const borderColor = meetsGoal === true ? '#16A34A'
    : meetsGoal === false && pctOfGoal !== null && pctOfGoal < 75 ? '#DC2626'
    : '#E2E8F0'; // very subtle default

  const openEdit = () => { setGoalInput(hasGoal ? String(Number(monthlyGoal)) : ''); setEditingGoal(true); setTimeout(() => inputRef.current?.focus(), 50); };
  const commitGoal = () => { const raw = goalInput.replace(/[^0-9.]/g, ''); const num = parseFloat(raw); onGoalChange?.(goalKey, isNaN(num) || raw === '' ? null : num); setEditingGoal(false); };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay: (index || 0) * 0.04, ease: 'easeOut' }}
    >
      <div className="kpi-card group" style={{ borderLeft: `3px solid ${borderColor}` }}>
        <div className="p-4 flex flex-col flex-1 justify-between">

          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider leading-tight flex-1 pr-2">
              {label}
            </p>

            {/* Goal icon — subtle, only visible on hover or when goal set */}
            <div className="flex items-center gap-1 flex-shrink-0">
              {hasGoal && pctOfGoal !== null && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button className={`text-xs font-semibold tabular-nums px-1.5 py-0.5 rounded-md transition-colors ${
                      meetsGoal
                        ? 'text-green-700 bg-green-50 hover:bg-green-100'
                        : pctOfGoal >= 75
                          ? 'text-amber-700 bg-amber-50 hover:bg-amber-100'
                          : 'text-red-600 bg-red-50 hover:bg-red-100'
                    }`}>
                      {pctOfGoal}%
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-4 bg-white shadow-[0_4px_24px_rgba(15,23,42,0.14)] border-slate-200 rounded-xl" side="top">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                        <CalendarDays size={13} className="text-blue-600" />
                        <p className="text-sm font-semibold text-slate-800">{label}</p>
                      </div>
                      <div className="space-y-1.5 text-xs">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Monthly target</span>
                          <span className="font-bold text-slate-900">{fmtVal(monthlyGoal, format)}</span>
                        </div>
                        {isCountKpi && periodDays && (
                          <div className="flex justify-between">
                            <span className="text-slate-500">Pro-rated ({Math.round(periodDays)}d)</span>
                            <span className="font-semibold text-slate-700">{fmtVal(proratedGoal, format)}</span>
                          </div>
                        )}
                        <div className="flex justify-between pt-1 border-t border-slate-100">
                          <span className="text-slate-600 font-medium">Current</span>
                          <span className={`font-bold text-sm ${meetsGoal ? 'text-green-700' : 'text-red-600'}`}>{fmtVal(numValue, format)}</span>
                        </div>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${
                          meetsGoal ? 'bg-green-500' : pctOfGoal >= 75 ? 'bg-amber-400' : 'bg-blue-500'
                        }`} style={{ width: `${Math.min(100, pctOfGoal)}%` }} />
                      </div>
                      <p className="text-xs text-slate-400">{pctOfGoal}% of {isCountKpi ? 'pro-rated' : 'monthly'} goal{meetsGoal && pctOfGoal > 100 ? ` (+${pctOfGoal - 100}% over)` : ''}</p>
                      <button onClick={() => onGoalChange?.(goalKey, null)} className="text-xs text-slate-400 hover:text-red-500">Clear goal</button>
                    </div>
                  </PopoverContent>
                </Popover>
              )}

              {/* Set goal button — only on hover */}
              {goalKey && onGoalChange && (
                multiLocation ? (
                  <button onClick={() => navigate('/goals')}
                    className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-slate-100">
                    <Target size={11} className="text-slate-300" />
                  </button>
                ) : (
                  <Popover open={editingGoal} onOpenChange={o => !o && setEditingGoal(false)}>
                    <PopoverTrigger asChild>
                      <button onClick={openEdit}
                        className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-slate-100">
                        <Target size={11} className={hasGoal ? 'text-slate-300' : 'text-slate-300'} />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-52 p-3 bg-white shadow-[0_4px_24px_rgba(15,23,42,0.14)] border-slate-200 rounded-xl" side="top">
                      <p className="text-xs font-semibold text-slate-700 mb-1">{label}</p>
                      <p className="text-xs text-slate-400 mb-2">{isCountKpi ? 'Monthly target — auto pro-rated' : 'Fixed rate target'}</p>
                      <div className="flex gap-2">
                        <Input ref={inputRef} value={goalInput} onChange={e => setGoalInput(e.target.value)}
                          placeholder={format === 'currency' ? '80000' : format === 'percent' ? '40' : '25'}
                          className="h-7 text-xs"
                          onKeyDown={e => { if (e.key === 'Enter') commitGoal(); if (e.key === 'Escape') setEditingGoal(false); }} />
                        <Button size="sm" className="h-7 w-7 p-0" onClick={commitGoal}><Check size={12} /></Button>
                      </div>
                      {hasGoal && <button onClick={() => { onGoalChange?.(goalKey, null); setEditingGoal(false); }} className="text-xs text-slate-400 hover:text-red-500 mt-1.5 block">Clear</button>}
                    </PopoverContent>
                  </Popover>
                )
              )}
            </div>
          </div>

          {/* Main value */}
          <div className="flex-1 flex items-end">
            <p className={`font-heading text-3xl font-semibold tracking-tight tabular-nums leading-none ${
              meetsGoal === true ? 'text-slate-900'
              : meetsGoal === false && pctOfGoal < 75 ? 'text-slate-900'
              : 'text-slate-900'
            }`}>
              {fmtVal(value, format)}
            </p>
          </div>

          {/* Bottom info — sub value OR goal progress, mutually exclusive */}
          <div className="mt-2">
            {hasGoal && pctOfGoal !== null ? (
              <div>
                <div className="h-1 bg-slate-100 rounded-full overflow-hidden mb-1">
                  <div className={`h-full rounded-full transition-all duration-700 ${
                    meetsGoal ? 'bg-green-400' : pctOfGoal >= 75 ? 'bg-amber-300' : 'bg-blue-300'
                  }`} style={{ width: `${Math.min(100, pctOfGoal)}%` }} />
                </div>
                <p className="text-xs text-slate-400">
                  Goal: {fmtVal(proratedGoal, format)}{subLabel ? ` · ${subValue !== undefined ? fmtVal(subValue, format) : ''} ${subLabel}` : ''}
                </p>
              </div>
            ) : (subValue !== undefined || subLabel) ? (
              <p className="text-xs text-slate-400">
                {subValue !== undefined ? fmtVal(subValue, format) : ''}{subLabel ? ` ${subLabel}` : ''}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
